/* Mari'Anna Tees: "Standard | Add your name" option.
   - Product page: shows a name box only when "Add your name" is picked.
   - Add to cart: sends the item (with Name saved as a line item property) plus the
     hidden add-on charge in one request, linked by a private _mt_link id.
   - Cart: keeps each add-on charge matched 1:1 with its named item (removes orphan
     charges, re-adds a missing charge, keeps quantities equal). */
(() => {
  if (window.MTPersonalize) return;

  const ADDON_HANDLE = 'personalization-add-your-name';
  const NAME_RE = /^[\p{L}\p{N} .'&-]+$/u;

  const rootOf = (form) => form && form.querySelector('[data-mt-pz]');
  const isOn = (root) => !!(root && root.querySelector('input[name="mt_pz_choice"][value="name"]')?.checked);

  function setState(root, userAction) {
    const on = isOn(root);
    const panel = root.querySelector('[data-mt-panel]');
    const input = root.querySelector('[data-mt-name]');
    if (panel) panel.hidden = !on;
    if (input) {
      input.disabled = !on;
      input.removeAttribute('aria-invalid');
      if (on && userAction) input.focus({ preventScroll: true });
    }
    const pf = root.closest('product-form');
    if (pf) pf.toggleAttribute('data-mt-pz-on', on);
  }

  document.addEventListener('change', (e) => {
    if (e.target?.name !== 'mt_pz_choice') return;
    const root = e.target.closest('[data-mt-pz]');
    if (root) setState(root, true);
  });

  document.addEventListener('input', (e) => {
    if (e.target?.matches?.('[data-mt-name]')) e.target.removeAttribute('aria-invalid');
  });

  function validate(form) {
    const root = rootOf(form);
    if (!isOn(root)) return true;
    const input = root.querySelector('[data-mt-name]');
    const value = (input.value || '').replace(/\s+/g, ' ').trim();
    input.value = value;
    const max = input.maxLength > 0 ? input.maxLength : 14;
    let msg = '';
    if (!value) msg = 'Type the name you want on it, or choose Standard.';
    else if (value.length > max) msg = `Names can be up to ${max} characters.`;
    else if (!NAME_RE.test(value)) msg = "Use letters, numbers, spaces and . ' & - only.";
    if (!msg) return true;
    input.setAttribute('aria-invalid', 'true');
    input.focus();
    return msg;
  }

  // Returns a JSON items array for /cart/add.js when a name is chosen, otherwise null.
  function buildItems(formData, form) {
    const root = rootOf(form);
    if (!isOn(root)) return null;
    const quantity = parseInt(formData.get('quantity'), 10) || 1;
    const link = 'mt' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const m = key.match(/^properties\[(.+)\]$/);
      if (m && value !== '') properties[m[1]] = value;
    }
    properties._mt_link = link;
    properties._mt_addon = root.dataset.addonId;
    const main = { id: Number(formData.get('id')), quantity, properties };
    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) main.selling_plan = Number(sellingPlan);
    return [
      main,
      {
        id: Number(root.dataset.addonId),
        quantity,
        properties: { For: root.dataset.productTitle, _mt_link: link },
      },
    ];
  }

  // ---- Cart sync -------------------------------------------------------------
  let syncing = false;

  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  }

  async function refreshCartUI() {
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=cart-icon-bubble`);
      const data = await res.json();
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && data['cart-icon-bubble']) {
        const html = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html');
        const inner = html.querySelector('.shopify-section');
        if (inner) bubble.innerHTML = inner.innerHTML;
      }
    } catch (e) {
      /* bubble refresh is cosmetic */
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mt-personalize' });
    }
  }

  async function syncCart() {
    if (syncing) return;
    syncing = true;
    try {
      const cartUrl = (window.routes?.cart_url || '/cart') + '.js';
      const cart = await (await fetch(cartUrl, { headers: { Accept: 'application/json' } })).json();
      const items = cart.items || [];
      const addons = items.filter((i) => i.handle === ADDON_HANDLE);
      const mains = items.filter((i) => i.handle !== ADDON_HANDLE && i.properties?._mt_link);

      const updates = {};
      const toAdd = [];

      for (const addon of addons) {
        const link = addon.properties?._mt_link;
        const main = link && mains.find((m) => m.properties._mt_link === link);
        const want = main ? main.quantity : 0;
        if (addon.quantity !== want) updates[addon.key] = want;
      }
      for (const main of mains) {
        const link = main.properties._mt_link;
        const has = addons.some((a) => a.properties?._mt_link === link);
        const addonId = Number(main.properties._mt_addon);
        if (!has && addonId) {
          toAdd.push({
            id: addonId,
            quantity: main.quantity,
            properties: { For: main.product_title, _mt_link: link },
          });
        }
      }

      let changed = false;
      if (Object.keys(updates).length) {
        await postJSON(window.routes?.cart_update_url || '/cart/update.js', { updates });
        changed = true;
      }
      if (toAdd.length) {
        await postJSON(window.routes?.cart_add_url || '/cart/add.js', { items: toAdd });
        changed = true;
      }
      if (changed) await refreshCartUI();
    } catch (e) {
      console.error('MTPersonalize cart sync', e);
    } finally {
      syncing = false;
    }
  }

  function init() {
    document.querySelectorAll('[data-mt-pz]').forEach((root) => setState(root, false));
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source === 'mt-personalize') return;
        syncCart();
      });
    }
    syncCart();
  }

  window.MTPersonalize = { validate, buildItems, syncCart };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
